import type { Enums } from "@/lib/database.types";

/* W10 Context Packs vocabulary (WORKSHOP.md §W10, design in
 * docs/WORKSHOP-W10.md), shared by the pack actions, the resolver queries, and
 * both UIs. Plain module (no "use server") so client components can import the
 * consts. */

export type PackItemKind = Enums<"pack_item_kind">;
export type PackRefMode = Enums<"pack_ref_mode">;
export type PackVisibility = Enums<"pack_visibility">;
export type PackProgressStatus = Enums<"pack_progress_status">;

/** Kinds that reference published book content (and so carry a reference mode
 * + source book). Everything else is structural / authored-in-place. */
export const CONTENT_KINDS = [
  "book",
  "rule",
  "command_card",
  "recipe",
  "field_note",
  "failure_note",
] as const satisfies readonly PackItemKind[];

export function isContentKind(kind: PackItemKind): boolean {
  return (CONTENT_KINDS as readonly string[]).includes(kind);
}

/** The FK column on pack_items that a content kind populates. */
export function contentColumnFor(kind: PackItemKind):
  | "book_id"
  | "rule_id"
  | "command_card_id"
  | "recipe_id"
  | "field_note_id"
  | "failure_note_id"
  | null {
  switch (kind) {
    case "book":
      return "book_id";
    case "rule":
      return "rule_id";
    case "command_card":
      return "command_card_id";
    case "recipe":
      return "recipe_id";
    case "field_note":
      return "field_note_id";
    case "failure_note":
      return "failure_note_id";
    default:
      return null;
  }
}

export const KIND_META: Record<PackItemKind, { label: string; hint: string }> = {
  section: { label: "Section", hint: "A heading that groups what follows" },
  book: { label: "Rule book", hint: "A whole book, in reading order" },
  rule: { label: "Rule", hint: "A single rule" },
  command_card: { label: "Command card", hint: "A copy-ready command" },
  recipe: { label: "Recipe", hint: "A multi-step procedure" },
  field_note: { label: "Field note", hint: "A working observation" },
  failure_note: { label: "Failure note", hint: "A known failure and its fix" },
  note: { label: "Guidance", hint: "Project-specific guidance you write here" },
  access: { label: "Access", hint: "How to request the access needed (no secrets)" },
  link: { label: "Link", hint: "An external resource" },
  checklist: { label: "Checklist", hint: "A list to work through" },
};

export const VISIBILITY_META: Record<PackVisibility, { label: string; hint: string }> = {
  private: { label: "Private", hint: "Only you can see this pack" },
  unlisted: { label: "Unlisted", hint: "Anyone with the link can open it" },
  public: { label: "Public", hint: "Listed and discoverable" },
};

/* Staleness — computed in the resolver, rendered by the consumer view. */
export type PackStaleness =
  | { kind: "none" }
  | { kind: "source_updated" } // a live item whose source book moved past reviewed_at
  | { kind: "newer_edition" }; // a pinned item whose book has advanced

/** Split a checklist body (one item per line) into trimmed lines. */
export function checklistLines(body: string | null | undefined): string[] {
  return (body ?? "")
    .split("\n")
    .map((l) => l.replace(/^\s*[-*•]\s*/, "").trim())
    .filter(Boolean);
}
